import React, { useEffect, useMemo, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Flex, Box, Text, Button } from "@chakra-ui/react";
import { MessageSquare, EllipsisVertical } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchReviewsAction } from "state/actions/reviewActions";

const RatingManagement = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { reviews = [], pagination, loading } = useSelector((state) => state.reviews);

  const [page, setPage] = useState(1);
  const size = 50;

  useEffect(() => {
    dispatch(fetchReviewsAction({ mode: "all", page, size }));
  }, [dispatch, page]);

  const generateMenuItems = (row) => [
    {
      icon: <MessageSquare size={16} />,
      text: "Chat with User",
      onClick: () => history.push("/admin/messages"),
    },
  ];

  const getDisplayName = (user) => {
    if (!user) return "Unknown";
    const first = (user.first_name || "").trim();
    const last = (user.last_name || "").trim();
    const full = `${first} ${last}`.trim();
    if (full) return full;
    if (user.username) return user.username.trim();
    if (user.email) return user.email;
    return "No name available";
  };

  const getReviewedParty = (row) => {
    if (row?.review?.driver_id) return { label: "Driver", user: row.driver_user_info };
    if (row?.review?.target_user_id) return { label: row?.review?.rating_type || "User", user: row.target_user_info };
    return { label: "N/A", user: null };
  };

  const columnsData = useMemo(
    () => [
      {
        Header: "Reviewer Name",
        accessor: "reviewer_name",
        Cell: ({ row }) => {
          const name = row.original.reviewer_name || "Unknown";
          const phone = row.original.reviewer_phone || "N/A";
          const village = row.original.reviewer_village || "N/A";
          return `${name} (${phone}, ${village})`;
        },
      },
      {
        Header: "Reviewed Party",
        accessor: "reviewed_name",
        Cell: ({ row }) => {
          const label = row.original.reviewed_label || "N/A";
          const name = row.original.reviewed_name || "Unknown";
          const phone = row.original.reviewed_phone || "N/A";
          const village = row.original.reviewed_village || "N/A";
          return `${label}: ${name} (${phone}, ${village})`;
        },
      },
      { Header: "Rating Type", accessor: "rating_type" },
      {
        Header: "Rating",
        accessor: "rating",
        Cell: ({ value }) => {
          const numeric = Number(value) || 0;
          return (
            <Flex>
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} style={{ color: i < Math.round(numeric) ? "gold" : "lightgray" }}>
                  ★
                </span>
              ))}
            </Flex>
          );
        },
      },
      { Header: "Review", accessor: "review" },
      {
        Header: "Review Date",
        accessor: "review_date",
        Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : "N/A"),
      },
      {
        Header: "Actions",
        accessor: "moreOptions",
        Cell: ({ row }) => (
          <ThreeDotMenu
            icon={<EllipsisVertical size={20} color="grey" />}
            items={generateMenuItems(row.original)}
          />
        ),
      },
    ],
    []
  );

  const formattedReviews = useMemo(() => {
    return (reviews || []).map((row) => {
      const reviewer = row.reviewer_info;
      const reviewed = getReviewedParty(row);

      return {
        review_id: row.review?.review_id,

        reviewer_name: getDisplayName(reviewer),
        reviewer_phone: reviewer?.phone_number || "N/A",
        reviewer_village: reviewer?.village || "N/A",

        reviewed_label: reviewed.label,
        reviewed_name: getDisplayName(reviewed.user),
        reviewed_phone: reviewed.user?.phone_number || "N/A",
        reviewed_village: reviewed.user?.village || "N/A",

        rating_type: row.review?.rating_type,
        rating: row.review?.rating,
        review: row.review?.review,
        review_date: row.review?.review_date,
      };
    });
  }, [reviews]);

  const total = pagination?.total ?? 0;
  const pages = pagination?.pages ?? 1;

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center">
          {loading ? (
            <Box width="200px">
              <Skeleton height="20px" />
            </Box>
          ) : (
            <Text fontSize="md" fontWeight="bold">
              All Reviews: {total}
            </Text>
          )}

          <Flex gap={2} alignItems="center">
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              isDisabled={loading || page <= 1}
            >
              Prev
            </Button>
            <Text fontSize="sm">
              Page {page} / {pages}
            </Text>
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              isDisabled={loading || page >= pages}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Box>

      <ReusableTable columnsData={columnsData} tableData={formattedReviews} isLoading={loading} />
    </Flex>
  );
};

export default RatingManagement;
